/** Typer for Bonnetid-verktøyene under «Eksterne kilder» (speiler backend-DTO-ene). */

export type BonnetidCatalogSource = "NEW" | "LEGACY";

export interface ExternalJamatRegistration {
  id: number;
  orgNr: string;
  mosqueName: string | null;
  source: string;
  isActive: boolean;
  firstSyncedAt: string;
  /** Siste nattsynk eller manuelle synk. */
  lastSyncedAt: string;
  /** Siste synk ved bruk (noen åpnet moskeen og tidene manglet). */
  lastLazySyncedAt?: string | null;
  bonnetidJamatDays: number;
  bonnetidJummahRows: number;
  /** Bonnetid-ID tidene hentes fra (moskeens org.nr. med mindre kilden er byttet). */
  sourceOrgNr?: string;
  sourceChanged?: boolean;
}

export interface ExternalSyncLog {
  id: number;
  mode: string;
  startedAt: string;
  finishedAt: string;
  mosquesProcessed: number;
  mosquesSkipped: number;
  jamatDaysCreated: number;
  jamatDaysSkipped: number;
  jummahRowsCreated: number;
  jummahRowsReplaced: number;
  errors: string[];
  success: boolean;
  note?: string | null;
  /** Manuell synk av én moské: hva som ble hentet kan vises (GET sync-logs/{id}/details). */
  hasDetails?: boolean;
}

/** Hva synken gjorde med én dag. */
export type SyncDayAction =
  | "CREATED"
  | "REPLACED"
  | "SKIPPED_ADMIN"
  | "SKIPPED_MY_MASJID"
  | "SKIPPED_SAME"
  | "SKIPPED_PAST"
  | "SKIPPED_FEWER_FILLED"
  | "SKIPPED_EXISTS";

export interface SyncDay {
  /** «dd-MM». */
  date: string;
  action: SyncDayAction | string;
  existingSource?: string | null;
  /** fajr, duhr, asr, maghrib, isha som lå der fra før. */
  existing?: Array<string | null> | null;
}

export interface SyncPeriod {
  startDate: string | null;
  endDate: string | null;
  fajr: string | null;
  duhr: string | null;
  asr: string | null;
  maghrib: string | null;
  isha: string | null;
  offsets: Record<string, number>;
}

/** GET /api/v1/admin/external-jamat/sync-logs/{id}/details. */
export interface SyncDetails {
  orgNr: string;
  sourceOrgNr: string;
  request: string;
  response: unknown;
  period: SyncPeriod | null;
  days: SyncDay[];
  summary: Record<string, number>;
}

export const SYNC_ACTION_LABELS: Record<string, string> = {
  CREATED: "Lagt inn",
  REPLACED: "Erstattet",
  SKIPPED_ADMIN: "Beholdt – moskeens egne tider",
  SKIPPED_MY_MASJID: "Beholdt – My-Masjid",
  SKIPPED_SAME: "Uendret",
  SKIPPED_PAST: "Beholdt – dagen er passert",
  SKIPPED_FEWER_FILLED: "Beholdt – har flere tider",
  SKIPPED_EXISTS: "Beholdt – finnes fra før",
};

/** En rad i Bonnetid-katalogen (samme endepunkt som import-lista). */
export interface CatalogCandidate {
  orgNr: string;
  name: string;
  address: string;
  postalCode: string;
  city: string;
  existing: boolean;
  possibleMatch: boolean;
}

export interface SourceChangeResult {
  previousSourceOrgNr: string;
  registration: ExternalJamatRegistration;
  syncLog: ExternalSyncLog;
}

export interface CatalogMosqueRef {
  orgNr: string;
  name: string;
  address: string;
  postalCode: string;
  city: string;
}

export interface FetchedMosque {
  orgNr: string;
  name: string | null;
  registrationId: number | null;
  registrationCreated: boolean;
  linkedBySourceChange: boolean;
  fieldsFilled: Array<{ field: string; value: string | null }>;
  syncLog: ExternalSyncLog | null;
}

/** Svaret fra GET /api/v1/admin/external-jamat/raw: rådata for én moské, ingenting lagres. */
export interface BonnetidRawLookup {
  source: BonnetidCatalogSource;
  orgNr: string;
  /** Forespørselen som ble sendt, uten nøkkel. */
  request: string;
  httpStatus: number | null;
  durationMs: number;
  body?: unknown;
  bodyText?: string | null;
  error?: string | null;
}

export interface FetchOneResult {
  sourceOrgNr: string;
  sourceName: string | null;
  inCatalog: boolean;
  mosques: FetchedMosque[];
  prayerTimes: string[];
  errors: string[];
  message: string;
}

export interface RegisteredNotInCatalog {
  registrationId: number;
  orgNr: string;
  mosqueName: string | null;
  sourceOrgNr: string;
  isActive: boolean;
  suggestions: CatalogMosqueRef[];
}

export interface MissingTimes {
  registrationId: number;
  orgNr: string;
  mosqueName: string | null;
  sourceOrgNr: string;
  isActive: boolean;
  daysChecked: number;
  daysWithoutJamat: number;
  lastSyncedAt: string;
}

export interface CatalogCoverage {
  source: BonnetidCatalogSource;
  catalogMosques: number;
  registrations: number;
  missingInIbonn: CatalogMosqueRef[];
  registeredNotInCatalog: RegisteredNotInCatalog[];
  missingTimes: MissingTimes[];
}

/** Norske navn på moskéfeltene som kan fylles inn fra Bonnetid. */
export const FIELD_LABELS: Record<string, string> = {
  name: "Navn",
  orgName: "Organisasjonsnavn",
  streetAddress: "Adresse",
  postalCode: "Postnummer",
  city: "By",
  lat: "Breddegrad",
  lon: "Lengdegrad",
  logo: "Logo",
  website: "Nettside",
};

/** Bonnetid-ID slik backend godtar den (samme som i URL-en mot Bonnetid). */
export const isValidSourceId = (value: string): boolean =>
  /^[A-Za-z0-9_-]{1,32}$/.test(value.trim());
